/**
 * CalculateListContainer
 *
 *
 */

import React, { useEffect, useState } from 'react';
import CalculateListPresenter from './CalculateListPresenter';
import MessageAlert, { getCookie } from 'utils';
import {
  SettlementAmountApi,
  StatisticsApi,
  ShopInformationApi,
  // ParcelServiceApi,
} from 'api';
import moment from 'moment';
import { useAuthCheck } from 'hooks/AuthHooks';

const MOMENT_FORMAT = 'YYYY-MM-DD';
const initialDate = {
  year: moment().year(),
  month: moment().month() + 1,
  startDate: moment().startOf('month').format(MOMENT_FORMAT),
  endDate: moment().endOf('month').format(MOMENT_FORMAT),
};

/**----------------------------------------
 * [Component] 정산내역 - 컨테이너
 * ----------------------------------------
 */
const CalculateListContainer = ({ defaultShopId, permission }) => {
  /* ===== Mutate =====*/
  const { authCheck } = useAuthCheck();

  /* ===== ROUTE ===== */

  /* ===== PROPS ===== */

  /* ===== STATE ===== */
  const [loading, setLoading] = useState(false);
  const [shopList, setShopList] = useState([]);
  const [calculateList, setCalculateList] = useState([]);
  const [currentShop, setCurrentShop] = useState(defaultShopId ?? null);
  const [searchKeyword, setSearchKeyword] = useState('');
  const [currentDate, setCurrentDate] = useState(initialDate);
  const [selectedList, setSelectedList] = useState(null);
  const [isModalOpened, setIsModalOpened] = useState(false);
  const [stopAuth, setStopAuth] = useState(false);
  const [authInfo, setAuthInfo] = useState(null);
  // const [data, setData] = useState([]);
  // const [inputValue, setInputValue] = useState({});

  /* ===== VARIABLE ===== */

  const cookie = getCookie('JM_COFFEE_ACCESS_TOKEN');

  /* ===== FUNCTION ===== */
  /**
   * 어드민 권한 확인 함수
   */
  const handleTokenAuth = async () => {
    if (cookie) {
      const { status, data } = await authCheck();
      if (status !== 200) return;
      return data;
    }
  };
  /**--------------------
   * 월 증감
   * --------------------
   */
  const handleChangeMonth = (arrow) => {
    let newMonth = currentDate.month + (arrow === 'left' ? -1 : 1);
    let month, year;

    if (newMonth < 1) {
      month = 12;
      year = currentDate.year - 1;
    } else if (newMonth > 12) {
      month = 1;
      year = currentDate.year + 1;
    } else {
      month = newMonth;
      year = currentDate.year;
    }

    // 예외처리
    if (initialDate.month < month && initialDate.year <= year) {
      return MessageAlert.warning(
        `${initialDate.month}월이 최신 정산월입니다.`
      );
    }

    setCurrentDate({
      month,
      year,
      startDate: moment(`${year}-${month}`)
        .startOf('month')
        .format(MOMENT_FORMAT),
      endDate: moment(`${year}-${month}`).endOf('month').format(MOMENT_FORMAT),
    });
  };

  /**--------------------
   * 업체목록 조회
   * --------------------
   */
  const getShopInformations = async () => {
    try {
      const { status, message, data } =
        await ShopInformationApi.getShopInformations();
      if (status !== 200) throw { message };
      setShopList(data.filter((item) => item.type !== 'STORE'));
    } catch (e) {
      MessageAlert.error(e.message);
    }
  };

  /**--------------------
   * 정산내역 조회
   * --------------------
   */
  const getSettlementAmounts = async () => {
    if (!currentShop) return MessageAlert.warning('업체를 선택해주세요');
    setLoading(true);
    try {
      const { status, message, data } = await StatisticsApi.getStatistics(
        'settlement',
        {
          startDate: currentDate.startDate,
          endDate: currentDate.endDate,
          shopId: currentShop,
        }
      );
      if (status !== 200) throw { message };

      const mapData = data.map((_item, index) => {
        return {
          key: `${_item.shop_order_id}_${index}`,
          ..._item,
          payment_price: Number(_item.payment_price),
          product: JSON.parse(_item.product),
          ...(_item?.type === 'SUBSCRIPTION'
            ? { regular_payment_info: JSON.parse(_item.sub_data) }
            : _item?.type === 'RENTAL'
            ? { rental_payment_info: JSON.parse(_item.sub_data) }
            : {}),
        };
      });

      setTimeout(() => {
        setCalculateList(mapData);
        setLoading(false);
      }, 850);
    } catch (e) {
      setLoading(false);
      MessageAlert.error(e.message);
    }
  };

  /**--------------------
   * 정산금액 계산 함수
   * --------------------
   * @param {array} data
   * @returns
   */
  const handleGetCalculationPrice = (data = null) => {
    if (!data) return data;
    let totalPrice = 0;
    let totalCommissionPrice = 0;
    totalPrice = data.reduce((acc, cur) => acc + cur.payment_price, 0);
    totalCommissionPrice = data.reduce((acc, cur) => {
      const { payment_price } = cur;
      const commission_type = cur?.product?.commission?.commission_type || 'R';
      const commission_rate = cur?.product?.commission?.commission_rate ?? 0;
      const oneProductPrice = cur?.product?.price;
      if (commission_type === 'W') {
        return Math.ceil(
          acc + (payment_price / oneProductPrice) * commission_rate
        );
      } else {
        return Math.ceil(acc + (payment_price * commission_rate) / 100);
      }
    }, 0);
    return {
      // 결제금액 합계
      totalPrice,
      // 수수료 합계
      totalCommissionPrice,
      // 정산금액 = 결제금액합계 - 수수료합계
      calculationPrice: totalPrice - totalCommissionPrice,
    };
  };

  /**--------------------
   * 정산완료 => 출금신청으로 보내기
   * --------------------
   */
  const handleCalculation = async (admin = true) => {
    // setLoading(true);
    try {
      // 정산금액 계산
      const calcResult = handleGetCalculationPrice(selectedList);
      const parcelList = selectedList?.filter(
        (item) => item?.type !== 'RENTAL'
      );
      const rentalList = selectedList?.filter(
        (item) => item?.type === 'RENTAL'
      );
      // 정산 항목의 키 추출
      const parcelCalcIdList = parcelList.map((item) => item.key);
      const rentalCalcIdList = rentalList.map((item) => item.key);
      // 상태변경을 위한 아이디 추출
      const parcel_service = parcelList.map((item) => ({
        parcel_service_id: item.parcel_service_id,
        ...(item.regular_payment_id && {
          regular_payment_id: Number(item.regular_payment_id),
        }),
      }));

      const rental = rentalList.map((item) => ({
        rental_id: item.rental_id,
        rental_payment_id: Number(item.rental_payment_id),
      }));

      const amountStatus = permission === 'SHOP_ADMIN' ? 'UNDECIDED' : 'WAIT';
      // 정산(settlement_amount)데이터 생성
      const newData = {
        /* 기본정보 */
        shop_information_id: currentShop,
        admin_id: 1,
        company_name: 'JM',
        commission_rate: calcResult.totalCommissionPrice,
        sale_num: parcelCalcIdList.length + rentalCalcIdList.length,
        sale_price: calcResult.totalPrice,
        settlement_amount_price: calcResult.calculationPrice,
        vat: calcResult.calculationPrice * 0.1, // (매출액 - 매입값) * 10%
        settlement_amount_status: amountStatus, // [확정: DECIDED / 미확정: UNDECIDED / 정산만 완료: WAIT]
        /* 정산정보 */
        settlement_list: {
          settlement_date: `${currentDate.year}-${currentDate.month}`,
          ...(parcelCalcIdList?.length && { parcel_service: parcelCalcIdList }),
          ...(rentalCalcIdList?.length && { rental: rentalCalcIdList }),
        },

        /* 상태변경이 필요한 주문 */
        update_list: {
          // order: [],
          ...(parcelCalcIdList?.length && { parcel_service: parcel_service }),
          ...(rentalCalcIdList?.length && { rental: rental }),
        },

        /* 
        update_list: {
          // 상태변경이 필요한 오더 주문 건
          order: [{order_id :  1}, {order_id :  2}],

          // 상태변경이 필요한 일반/정기배송 상품 주문 건
          parcel_service: [ 
            {parcel_service_id : 1, ragular_payment_id :1}
            {parcel_service_id : 1, ragular_payment_id :2}
            {parcel_service_id : 2}
          ],

          // 상태변경이 필요한 렌탈 상품 주문 건
          rental: [{rental : 1, rental_payment_id :1}]
        } 
        */
      };
      // API => 정산등록 성공 시 일괄 상태변경
      const { status, message, data } =
        await SettlementAmountApi.createSettlementAmount(newData);
      if (status !== 200) throw { message };
      setTimeout(() => {
        MessageAlert.info(
          '정산처리되었습니다. 출금신청 페이지에서 최종확인 해주세요'
        );
        setIsModalOpened(false);
        setLoading(false);
        getSettlementAmounts();
      }, 750);
    } catch (e) {
      setLoading(false);
      MessageAlert.error(e.message);
    }
  };

  /* ===== HOOKS ===== */
  useEffect(() => {
    if (stopAuth || authInfo) return;
    const fetchAuth = async () => {
      const result = await handleTokenAuth();
      setAuthInfo(result);
      setStopAuth(true);
    };
    fetchAuth();
  }, []);
  /**--------------------
   * 최초 접속 시 업체목록 조회
   * --------------------
   */
  useEffect(() => {
    getShopInformations();
  }, []);

  /**--------------------
   * 업체 변경 시 실행
   * --------------------
   */
  useEffect(() => {
    // currentShop && getSettlementAmounts();
    if (currentShop) {
      getSettlementAmounts();
      setSelectedList(null);
    }
  }, [currentDate, currentShop]);

  /* ===== RENDER ===== */
  const props = {
    permission,
    loading,
    shopList:
      searchKeyword === ''
        ? shopList
        : shopList.filter(
            (item) => item.shop_information_name.indexOf(searchKeyword) >= 0
          ),
    calculateList,
    currentShop,
    searchKeyword,

    setSearchKeyword,
    setCurrentShop,
    currentDate,
    onChangeMonth: handleChangeMonth,
    selectedList,
    setSelectedList,
    onCalculation: handleCalculation,
    onGetCalculationPrice: handleGetCalculationPrice,
    isModalOpened,
    setIsModalOpened,
    authInfo: authInfo,
  };
  return (
    <>
      <CalculateListPresenter {...props} />
    </>
  );
};

CalculateListContainer.defaultProps = {
  defaultShopId: null,
  permission: 'ADMIN',
};
export default CalculateListContainer;
